import apiClient from '../axios.js';

/**
 * 抽獎工具管理 API
 */
export const lotteryService = {
  /**
   * 獲取抽獎配置列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 抽獎配置列表
   */
  getConfigs: async (params = {}) => {
    const response = await apiClient.get('/admin/lottery/configs', { params });
    return response;
  },

  /**
   * 獲取單一抽獎配置詳情
   * @param {string} id - 配置 ID
   * @returns {Promise<Object>} 抽獎配置詳情
   */
  getConfig: async (id) => {
    const response = await apiClient.get(`/admin/lottery/configs/${id}`);
    return response;
  },

  /**
   * 獲取單一抽獎配置詳情（別名，用於編輯頁面）
   * @param {string} id - 配置 ID
   * @returns {Promise<Object>} 抽獎配置詳情
   */
  getConfigById: async (id) => {
    const response = await apiClient.get(`/admin/lottery/configs/${id}`);
    return response;
  },

  /**
   * 建立抽獎配置
   * @param {Object} configData - 配置資料
   * @returns {Promise<Object>} 建立的配置
   */
  createConfig: async (configData) => {
    const response = await apiClient.post('/admin/lottery/configs', configData);
    return response;
  },

  /**
   * 更新抽獎配置
   * @param {string} id - 配置 ID
   * @param {Object} configData - 更新資料
   * @returns {Promise<Object>} 更新的配置
   */
  updateConfig: async (id, configData) => {
    const response = await apiClient.put(`/admin/lottery/configs/${id}`, configData);
    return response;
  },

  /**
   * 刪除抽獎配置
   * @param {string} id - 配置 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteConfig: async (id) => {
    const response = await apiClient.delete(`/admin/lottery/configs/${id}`);
    return response;
  },

  /**
   * 獲取獎品列表
   * @param {string} configId - 配置 ID
   * @returns {Promise<Array>} 獎品列表
   */
  getItems: async (configId) => {
    const response = await apiClient.get(`/admin/lottery/configs/${configId}/items`);
    return response;
  },

  /**
   * 建立獎品
   * @param {string} configId - 配置 ID
   * @param {Object} itemData - 獎品資料
   * @returns {Promise<Object>} 建立的獎品
   */
  createItem: async (configId, itemData) => {
    const response = await apiClient.post(`/admin/lottery/configs/${configId}/items`, itemData);
    return response;
  },

  /**
   * 批次儲存獎品
   * @param {string} configId - 配置 ID
   * @param {Array} items - 獎品陣列
   * @returns {Promise<Array>} 儲存的獎品列表
   */
  batchSaveItems: async (configId, items) => {
    const response = await apiClient.put(`/admin/lottery/configs/${configId}/items/batch`, { items });
    return response;
  },

  /**
   * 更新獎品
   * @param {string} itemId - 獎品 ID
   * @param {Object} itemData - 更新資料
   * @returns {Promise<Object>} 更新的獎品
   */
  updateItem: async (itemId, itemData) => {
    const response = await apiClient.put(`/admin/lottery/items/${itemId}`, itemData);
    return response;
  },

  /**
   * 刪除獎品
   * @param {string} itemId - 獎品 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteItem: async (itemId) => {
    const response = await apiClient.delete(`/admin/lottery/items/${itemId}`);
    return response;
  },

  /**
   * 獲取抽獎記錄列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 抽獎記錄列表
   */
  getRecords: async (params = {}) => {
    const response = await apiClient.get('/admin/lottery/records', { params });
    return response;
  },

  /**
   * 獲取抽獎統計
   * @param {string} configId - 配置 ID
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 統計資料
   */
  getStatistics: async (configId, params = {}) => {
    const response = await apiClient.get(`/admin/lottery/configs/${configId}/stats`, { params });
    return response;
  },

  /**
   * 取得視覺配置
   * @param {string} configId - 配置 ID
   * @returns {Promise<Object>} 視覺配置
   */
  getVisualConfig: async (configId) => {
    const response = await apiClient.get(`/admin/lottery/configs/${configId}/visual-config`);
    return response;
  },

  /**
   * 更新視覺配置
   * @param {string} configId - 配置 ID
   * @param {Object} visualConfig - 視覺配置
   * @returns {Promise<Object>} 更新結果
   */
  updateVisualConfig: async (configId, visualConfig) => {
    const response = await apiClient.put(`/admin/lottery/configs/${configId}/visual-config`, {
      visualConfig
    });
    return response;
  },

  /**
   * 取得工具配置
   * @param {string} configId - 配置 ID
   * @returns {Promise<Object>} 工具配置
   */
  getToolConfig: async (configId) => {
    const response = await apiClient.get(`/admin/lottery/configs/${configId}/tool-config`);
    return response;
  },

  /**
   * 更新工具配置
   * @param {string} configId - 配置 ID
   * @param {Object} toolConfig - 工具配置
   * @returns {Promise<Object>} 更新結果
   */
  updateToolConfig: async (configId, toolConfig) => {
    const response = await apiClient.put(`/admin/lottery/configs/${configId}/tool-config`, {
      toolConfig
    });
    return response;
  },

  /**
   * 取得圖片上傳預簽名 URL
   * @param {string} configId - 配置 ID
   * @param {Object} uploadData - 上傳資料 { fileName, fileType, fileSize }
   * @returns {Promise<Object>} 上傳 URL 資訊
   */
  getUploadUrl: async (configId, uploadData) => {
    const response = await apiClient.post(`/admin/lottery/configs/${configId}/get-upload-url`, uploadData);
    return response;
  },

  /**
   * 確認圖片上傳完成
   * @param {string} configId - 配置 ID
   * @param {Object} confirmData - 確認資料 { fileKey, fileUrl }
   * @returns {Promise<Object>} 確認結果
   */
  confirmUpload: async (configId, confirmData) => {
    const response = await apiClient.post(`/admin/lottery/configs/${configId}/confirm-upload`, confirmData);
    return response;
  },

  /**
   * 上傳圖片（本地端）
   * @param {string} configId - 配置 ID
   * @param {File} file - 圖片文件
   * @returns {Promise<Object>} 上傳結果
   */
  uploadImage: async (configId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `/admin/lottery/configs/${configId}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response;
  },

  /**
   * 刪除抽獎紀錄
   * @param {string} id - 紀錄 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteRecord: async (id) => {
    const response = await apiClient.delete(`/admin/lottery/records/${id}`);
    return response;
  },

  /**
   * 刪除圖片
   * @param {string} configId - 配置 ID
   * @param {string} fileUrl - 圖片 URL
   * @param {string} objectKey - S3 物件鍵（可選）
   * @returns {Promise<Object>} 刪除結果
   */
  deleteImage: async (configId, fileUrl, objectKey = null) => {
    const response = await apiClient.post(`/admin/lottery/configs/${configId}/delete-image`, {
      fileUrl,
      objectKey
    });
    return response;
  }
};
